import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import reportsQueueTableUtils from './reportsQueueTableUtils';
import Utils from 'utils';
import commonStrings from 'resources/commonStrings';
import WithAnalytics from 'hocs/withAnalytics';
import Settings from 'resources/settings';
import batchReportApi from '../../http-clients/batch-report-api';

import style from './ReportsQueueTable.module.css';
import Checkbox, { labelPositions } from '../Checkbox';
import checkboxStyle from '../Checkbox/Checkbox.module.css';
import Popover from '../Popover';
import ReportsQueueNoData from './ReportsQueueNoData';

const propTypes = {
  isUpdating: PropTypes.bool.isRequired,
  selectAll: PropTypes.oneOf(['ALL_SELECTED', 'NONE_SELECTED', 'INDETERMINATE']).isRequired,
  toggleSelectAll: PropTypes.func.isRequired,
  selectedRows: PropTypes.array.isRequired,
  toggleRow: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  isFirstDataLoad: PropTypes.bool.isRequired,
  error: PropTypes.object,
  onRetrySuccess: PropTypes.func,
};

const CheckboxWithAnalytics = WithAnalytics(Checkbox);
const RemoveSelectedWithAnalytics = WithAnalytics((props) => {
  return (
    <button
      className={props.removeSelectedClass}
      aria-label={commonStrings.queue.removeSelectedDisabledLabel}
      disabled={props.selectedRows.length <= 0}
      onClick={props.onClick}
      ref={props.removeSelectedRef}
    >
      <span tabIndex="-1">{commonStrings.queue.removeSelected}</span>
    </button>
  );
});
const SortableHeaderWithAnalytics = WithAnalytics((row) => {
  return (
    <div className={`${style[`table-header`]}`} aria-label={row.ariaLabel} onClick={row.onClick}>
      {row.content}
    </div>
  );
});

/**
 * Downloading from Batch Report
 * OnClick of 'Download' will get a pre-signed URL for download from Batch Report API
 * Create an <a> tag with the URL as href and click on it for user.
 */
const downloadBatchReportOnClick = async (row, setLoading = () => {}) => {
  try {
    setLoading(true);
    const original = (row && (row.original || row._original)) || row || {};
    const educatorBid = original.educatorBid || original.educator_bid;
    const requestParams = original.requestParams || original.request_params;
    const requestId = original.request_id || original.requestId;
    const response = await batchReportApi.getDownloadUrl(educatorBid, requestId, requestParams);
    const downloadUrl = response.download_url;

    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = '';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (error) {
    console.error('Download failed:', error);
  } finally {
    setLoading(false);
  }
};

const retryBatchReportOnClick = async (row, setLoading, onRetrySuccess) => {
  try {
    setLoading(true);
    const partnerBid = row._original.partnerBid;
    const requestId = row._original.requestId;
    const createdAt = row._original.createdAt;
    const requestParams = row._original.requestParams;
    await batchReportApi.retryReport(partnerBid, requestId, createdAt, requestParams);
    if (typeof onRetrySuccess === 'function') {
      // kick off table refresh to view updated status
      await onRetrySuccess();
    }
  } catch (error) {
    console.error('Retry failed:', error);
  } finally {
    setLoading(false);
  }
};

const RetryLinkWithAnalytics = WithAnalytics(
  class extends React.Component {
    constructor(props) {
      super(props);
      this.state = { isLoading: false };
    }

    setLoading = (loading) => {
      this.setState({ isLoading: loading });
    };

    render() {
      const { row } = this.props;
      const { isLoading } = this.state;

      return (
        <div className={style[`cell`]}>
          <a
            className={style[`action-cell`]}
            onClick={() => retryBatchReportOnClick(row, this.setLoading, this.props.onRetrySuccess)}
            style={{
              pointerEvents: isLoading ? 'none' : 'auto',
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            {isLoading && <LoadingSpinner />}
            {isLoading ? 'Retrying...' : commonStrings.queue.retry}
          </a>
        </div>
      );
    }
  }
);

// Simple CSS spinner component
const LoadingSpinner = () => (
  <span
    style={{
      display: 'inline-block',
      width: '16px',
      height: '16px',
      border: '2px solid #f3f3f3',
      borderTop: '2px solid #0066cc',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginRight: '8px',
    }}
  />
);

// Add CSS keyframes for spinner animation
if (typeof document !== 'undefined') {
  const spinnerStyle = document.createElement('style');
  spinnerStyle.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(spinnerStyle);
}

const DownloadLinkWithAnalytics = WithAnalytics(
  class extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        isLoading: false,
      };
    }

    setLoading = (loading) => {
      this.setState({ isLoading: loading });
    };

    render() {
      const { row } = this.props;
      const { isLoading } = this.state;
      if (row._original.isBatchReportYN) {
        /** Batch Report API reports */
        return (
          <div className={style[`cell`]}>
            <a
              className={style[`action-cell`]}
              onClick={() => downloadBatchReportOnClick(row, this.setLoading)}
              target="_blank"
              aria-label={`Download ${row.reportId}, created on ${Utils.formatDate(row.createdDate)}`}
              style={{
                pointerEvents: isLoading ? 'none' : 'auto',
                opacity: isLoading ? 0.6 : 1,
              }}
            >
              {isLoading && <LoadingSpinner />}
              {isLoading ? commonStrings.queue.downloading : commonStrings.queue.download}
            </a>
          </div>
        );
      }
      /** Legacy Reporting Gateway API reports */
      return (
        <div className={style[`cell`]}>
          <a
            className={style[`action-cell`]}
            href={`${Settings.ADMIN_BASE_PATH}/report/download/rpt/${row._original.reportRequestStatusId}`}
            onClick={row.onClick}
            target="_blank"
            aria-label={`Download ${row.reportId}, created on ${Utils.formatDate(row.createdDate)}`}
          >
            {commonStrings.queue.download}
          </a>
        </div>
      );
    }
  }
);

const defaultSorted = [
  {
    id: 'createdDate',
    desc: true,
  },
];
class ReportsQueueTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sorted: defaultSorted,
    };
    this.removeSelectedRef = React.createRef();
  }

  getSortableHeaderAriaLabel(columnId, headerName) {
    let thisColumnSorted = false;
    let sortDescription = `Column ${headerName}, Sorted`;

    this.state.sorted.forEach((s) => {
      if (s.id === columnId) {
        thisColumnSorted = true;
        if (s.desc === true) {
          sortDescription += ', descending';
        } else if (s.desc === false) {
          sortDescription += ', ascending';
        }
      }
    });

    if (!thisColumnSorted) {
      return `Column ${headerName}, Sortable`;
    }

    return sortDescription;
  }

  getNonSortableHeaderAriaLabel(headerName) {
    return `Column, ${headerName}`;
  }

  getColumns() {
    return [
      {
        Header: () => {
          let headerAriaLabel = null;
          switch (this.props.selectAll) {
            case 'INDETERMINATE':
              headerAriaLabel = commonStrings.queueCheckboxAriaLabel.indeterminateHeader;
              break;
            case 'ALL_SELECTED':
              headerAriaLabel = commonStrings.queueCheckboxAriaLabel.allSelectedHeader;
              break;
            default:
              headerAriaLabel = commonStrings.queueCheckboxAriaLabel.noneSelectedHeader;
          }
          return (
            <CheckboxWithAnalytics
              checked={this.props.selectAll === 'ALL_SELECTED'}
              indeterminate={this.props.selectAll === 'INDETERMINATE' ? 'true' : undefined}
              ariaLabel={headerAriaLabel}
              labelPosition={labelPositions.HIDDEN}
              onChange={this.props.toggleSelectAll}
              category={'rlp-queue-table'}
              action={'select-all-reports'}
              label={'multi-reports-select-button'}
              trackingProp={this.props.selectAll}
              additionalPayload={{ selectAllState: this.props.selectAll }}
            />
          );
        },
        accessor: '',
        width: 60,
        resizable: false,
        sortable: false,

        Cell: (row) => {
          const requestId = row.original.isBatchReportYN ? row.original.requestId : row.original.reportRequestStatusId;
          const isBatchReportYN = row.original.isBatchReportYN ? true : false;
          const toggleFn = () => this.props.toggleRow(requestId, isBatchReportYN);
          const isChecked = this.props.selectedRows.includes(requestId);
          return (
            <CheckboxWithAnalytics
              checked={this.props.selectedRows.includes(requestId)}
              ariaLabel={commonStrings.queueCheckboxAriaLabel.individualReport}
              labelPosition={labelPositions.HIDDEN}
              onChange={toggleFn}
              category={'rlp-queue-table'}
              action={'select-report'}
              label={'reportId-' + requestId}
              trackingProp={isChecked}
              additionalPayload={{ checked: isChecked }}
            />
          );
        },
      },
      {
        Header: (header) => {
          const content = (
            <>
              {commonStrings.queueTableHeader.dateCreated[0]}
              <br />
              {commonStrings.queueTableHeader.dateCreated[1]}
            </>
          );
          const label = this.getSortableHeaderAriaLabel(
            header.column.id,
            commonStrings.queueTableHeader.dateCreated[0] + ' ' + commonStrings.queueTableHeader.dateCreated[1]
          );
          return (
            <>
              <SortableHeaderWithAnalytics
                category={'rlp-queue-table'}
                action={'sort-reports'}
                label={header.column.id}
                ariaLabel={label}
                content={content}
              />
            </>
          );
        },
        Cell: (row) => {
          return <div className={style[`cell`]}>{Utils.formatDate(row.value)}</div>;
        },
        accessor: 'createdDate',
        id: 'createdDate',
        // 124 aligns this column with the RemoveSelected button on supported viewport sizes
        maxWidth: 114,
      },
      // REPORT TYPE
      {
        Header: (header) => {
          const label = this.getSortableHeaderAriaLabel(header.column.id, commonStrings.queueTableHeader.reportType);
          return (
            <SortableHeaderWithAnalytics
              category={'rlp-queue-table'}
              action={'sort-reports'}
              label={header.column.id}
              ariaLabel={label}
              content={commonStrings.queueTableHeader.reportType}
            />
          );
        },
        Cell: (row) => {
          let reportNameDescriptor = '';
          if (row.value.reportNameDescriptor) {
            reportNameDescriptor = row.value.reportNameDescriptor;
          }

          return (
            <div>
              <div className={style[`label`]}>{row.value.reportId}</div>
              <div className={style[`reportNameDescriptor`]}>{reportNameDescriptor}</div>
            </div>
          );
        },
        accessor: (d) => {
          return {
            reportId: d.reportId,
            reportNameDescriptor: d.reportNameDescriptor,
          };
        },
        id: 'reportType',
        minWidth: 250,
        style: { whiteSpace: 'normal' },
      },
      // DETAILS
      {
        Header: () => {
          return (
            <div
              className={style[`table-header`]}
              aria-label={this.getNonSortableHeaderAriaLabel(commonStrings.queueTableHeader.details)}
            >
              {commonStrings.queueTableHeader.details}
            </div>
          );
        },
        Cell: (row) => {
          return (
            <>
              <div className={style[`cell`]}>{row.value.outputFormat}</div>
              <div className={style[`cell`]}>
                {reportsQueueTableUtils.transformReportFileSize(row.value.reportFileSize)}
              </div>
            </>
          );
        },
        accessor: (d) => {
          return {
            outputFormat: d.outputFormat,
            reportFileSize: d.reportFileSize,
          };
        },
        id: 'details',
        sortable: false,
      },
      // REPORT ID
      {
        Header: (header) => {
          const label = this.getSortableHeaderAriaLabel(header.column.id, commonStrings.queueTableHeader.reportId);
          const content = commonStrings.queueTableHeader.reportId;
          return (
            <SortableHeaderWithAnalytics
              category={'rlp-queue-table'}
              action={'sort-reports'}
              label={header.column.id}
              ariaLabel={label}
              content={content}
            />
          );
        },
        Cell: (row) => <div className={style[`cell`]}>{row.value}</div>,
        accessor: 'reportRequestStatusId',
        id: 'reportId',
      },
      // STATUS
      {
        Header: (header) => {
          const label = this.getSortableHeaderAriaLabel(header.column.id, commonStrings.queueTableHeader.status);

          return (
            <SortableHeaderWithAnalytics
              category={'rlp-queue-table'}
              action={'sort-reports'}
              label={header.column.id}
              ariaLabel={label}
              content={commonStrings.queueTableHeader.status}
            />
          );
        },
        Cell: (row) => {
          if (row.value.requestStatus === 'COMPLETE' || row.value.requestStatus === 'SUCCESS') {
            return (
              <>
                <div className={style[`label`]}>{reportsQueueTableUtils.transformStatus(row.value.requestStatus)}</div>
                <div className={style[`cell`]}>
                  {commonStrings.queue.deletingOnDate(reportsQueueTableUtils.parseDeleteDate(row.value.createdDate))}
                </div>
              </>
            );
          } else {
            let progressText = '';
            if (row.value.reportProgress && row.value.reportProgress.length > 0) {
              progressText = row.value.reportProgress;
            }
            return (
              <>
                <div className={style[`label`]}>{reportsQueueTableUtils.transformStatus(row.value.requestStatus)}</div>
                <div className={style['progress']}>{progressText}</div>
              </>
            );
          }
        },
        accessor: (d) => {
          return {
            requestStatus: d.requestStatus,
            createdDate: d.createdDate,
            reportProgress: d.reportProgress,
          };
        },
        id: 'status',
        sortMethod: (aStatus, bStatus, desc) => {
          let a = reportsQueueTableUtils.transformStatus(aStatus.requestStatus);
          let b = reportsQueueTableUtils.transformStatus(bStatus.requestStatus);
          // force null and undefined to the bottom
          a = a === null || a === undefined ? '' : a;
          b = b === null || b === undefined ? '' : b;
          // force any string values to lowercase
          a = typeof a === 'string' ? a.toLowerCase() : a;
          b = typeof b === 'string' ? b.toLowerCase() : b;
          // Return either 1 or -1 to indicate a sort priority
          if (a > b) {
            return 1;
          }
          if (a < b) {
            return -1;
          }
          // returning 0, undefined or any falsey value will use subsequent sorts or
          // the index as a tiebreaker
          return 0;
        },
        minWidth: 150,
      },
      // ACTION
      {
        Header: () => {
          return (
            <div
              className={style[`table-header`]}
              aria-label={this.getNonSortableHeaderAriaLabel(commonStrings.queueTableHeader.action)}
            >
              {commonStrings.queueTableHeader.action}
            </div>
          );
        },
        Cell: (row) => {
          if (row.value.isBatchReportYN && row.value.canRetryYN && row.value.requestStatus === 'FAILED') {
            return (
              <RetryLinkWithAnalytics
                category={'rlp-queue-table'}
                action={'retry-link'}
                label={'reportId-' + row.original.reportRequestStatusId}
                additionalPayload={{ reportType: row.original.reportId }}
                onRetrySuccess={this.props.onRetrySuccess}
                {...row}
              />
            );
          }
          if (row.value.requestStatus === 'COMPLETE' || row.value.requestStatus === 'SUCCESS') {
            return (
              <DownloadLinkWithAnalytics
                category={'rlp-queue-table'}
                action={'download-link'}
                label={'reportId-' + row.original.reportRequestStatusId}
                additionalPayload={{ reportType: row.original.reportId }}
                {...row}
              />
            );
          }
          return null;
        },
        accessor: (d) => {
          return {
            reportRequestStatusId: d.reportRequestStatusId,
            requestStatus: d.requestStatus,
            reportId: d.reportId,
            createdDate: d.createdDate,
            isBatchReportYN: d.isBatchReportYN,
            canRetryYN: d.canRetryYN,
            retryCount: d.retryCount,
            createdAt: d.createdAt,
            requestId: d.requestId,
            requestParams: d.requestParams,
            partnerBid: d.partnerBid,
          };
        },
        id: 'action',
        sortable: false,
      },
    ];
  }

  /*
   * On first data load, tell the user we're fetching data.
   * On all other loads (refreshes), tell the user we're refreshing.
   */
  getDataLoadingText = () => {
    if (this.props.isFirstDataLoad) {
      return commonStrings.queue.fetchingData;
    }

    return this.props.isUpdating ? commonStrings.queue.refreshingData : null;
  };

  renderErrorPopover = () => {
    if (!this.props.error) {
      return null;
    }

    if (this.props.error.type === 'removeSelected') {
      return (
        <Popover
          closePopoverFn={this.props.closeErrorPopover}
          popoverStyle={style.popover}
          inputToFocus={this.removeSelectedRef}
        >
          {commonStrings.queue.removeSelectedFailurePopover}
        </Popover>
      );
    } else {
      return null;
    }
  };

  getNoDataComponent = () => {
    return this.props.isFirstDataLoad ? null : <ReportsQueueNoData />;
  };

  render() {
    const classNames = `${style['column']} ${style['width-12']} ${checkboxStyle.blue} ${style.container}`;
    const removeSelectedClass = this.props.selectedRows.length <= 0 ? `tooltipBase tooltipTopLeftAlign` : '';
    const selectedText =
      this.props.selectedRows.length === 1 ? '1 Report Selected' : `${this.props.selectedRows.length} Reports Selected`;
    return (
      <div className={classNames}>
        <div className={style.header}>
          <div className={style[`reports-selected`]}>{selectedText}</div>
          <RemoveSelectedWithAnalytics
            category={'rlp-queue-table'}
            action={'remove-selected'}
            label={'remove-selected-button'}
            additionalPayload={{ removedReportCount: this.props.selectedRows.length }}
            removeSelectedClass={`${removeSelectedClass} ${
              style[`remove-selected`]
            } nwea-ds-v1-button-base nwea-ds-v1-button-small nwea-ds-v1-button-blue nwea-ds-v1-button-flat`}
            clickFn={this.props.removeSelected}
            selectedRows={this.props.selectedRows}
            removeSelectedRef={this.removeSelectedRef}
          />
          {this.renderErrorPopover()}

          <div className={style[`loading-text`]}>{this.getDataLoadingText()}</div>
        </div>
        <ReactTable
          columns={this.getColumns()}
          data={this.props.data ? this.props.data : []}
          showPagination={false}
          minRows={0}
          NoDataComponent={this.getNoDataComponent}
          defaultSorted={defaultSorted}
          defaultPageSize={1000}
          getTheadThProps={(state, rowInfo, column, instance) => {
            // These lines enable better keyboard navigation and sorting on enter
            const customTabIndex = column.sortable === false ? -1 : 0;
            return {
              tabIndex: customTabIndex,
              onKeyPress: (e, handleOriginal) => {
                // if keypress is enter
                if (column.sortable !== false && e.which === 13) {
                  instance.sortColumn(column);
                }
              },
            };
          }}
          getTrProps={(state, rowInfo, column) => {
            return {
              style: {
                background:
                  this.props.isRemoving && this.props.selectedRows.includes(rowInfo.row.reportId)
                    ? 'var(--nwea-ds-v1-gray-300)'
                    : '',
              },
            };
          }}
          onSortedChange={(newSorted, column, shiftKey) => {
            this.setState({ sorted: newSorted });
          }}
        />
      </div>
    );
  }
}

ReportsQueueTable.propTypes = propTypes;

export default ReportsQueueTable;

export const UNIT_TEST_ONLY = {
  downloadBatchReportOnClick,
};
